// File: ForgotPassword.jsx
import { ErrorMessage, Field, Form, Formik } from 'formik';
import '../style.css';
import Input from '../../../components/_shared/Input/Input';
import { forgotPasswordSchema } from './validationSchema';
import { useDispatch } from 'react-redux';
import ReactSelect from '../../../components/ReactSelect/ReactSelect';
import {  setOtpSession } from '../_redux/authSlice';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchUserInfo, sendForgotPassword } from '../_redux/authAPI';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toasters from '../../../../utils/toasters/Toasters';

// Corrected hook to pass object param
const usePhoneNumbersByUsername = (username) => {
  return useQuery(
    ['user-phone-numbers', username],
    () => fetchUserInfo({ username }),
    {
      enabled: !!username && username.length >= 4,
      select: (data) => data,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );
};

const ForgotPassword = () => {
    const dispatch = useDispatch();
    const formikRef = useRef();
  
    const [usernameWatcher, setUsernameWatcher] = useState('');
    const [debouncedUsername, setDebouncedUsername] = useState('');
    const [usernameOptions, setUsernameOptions] = useState([]);
    const [phoneOptionsList, setPhoneOptionsList] = useState([]);
    const [recoveryMode, setRecoveryMode] = useState('userName');
    const navigate = useNavigate();
  
    // ADD this to track and debounce email manually
    const [emailInput, setEmailInput] = useState('');
    const [debouncedEmail, setDebouncedEmail] = useState('');
  
    const recoveryOptions = [
      { label: 'User Name', value: 'userName' },
      { label: 'Email', value: 'email' },
    ];
  
    // Watch for manual username changes
    useEffect(() => {
      const interval = setInterval(() => {
        const uname = formikRef.current?.values?.username;
        if (uname !== usernameWatcher) {
          setUsernameWatcher(uname || '');
        }
      }, 300);
      return () => clearInterval(interval);
    }, [usernameWatcher]);
  
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedUsername(usernameWatcher.trim());
      }, 500);
      return () => clearTimeout(handler);
    }, [usernameWatcher]);
  
    // Watch raw email field separately
    useEffect(() => {
      const interval = setInterval(() => {
        const email = formikRef.current?.values?.email?.trim() || '';
        if (email !== emailInput) {
          setEmailInput(email);
        }
      }, 300);
      return () => clearInterval(interval);
    }, [emailInput]);
  
    // Debounce emailInput
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedEmail(emailInput);
      }, 500);
      return () => clearTimeout(handler);
    }, [emailInput]);
  
   // Fetch usernames by debounced email
      useEffect(() => {
        const fetchUsernamesByEmail = async () => {
          try {
            const res = await fetchUserInfo({ email: debouncedEmail });
            
            // If API returns valid usernames
            if (res?.data?.length) {
              setUsernameOptions(
                res.data.map((user) => ({
                  label: user.username_tx,
                  value: user.username_tx,
                }))
              );
            } else {
              // no users found in a 200 response (edge case)
              setUsernameOptions([]);
              toasters({ type: "error", message: "Email is invalid" });
            }
          } catch (err) {

            // Show toast if email not found
            if (err?.response?.status === 404) {
              toasters({ type: "error", message: "Email is invalid" });
            } else {
              toasters({ type: "error", message: "Something went wrong" });
            }

            setUsernameOptions([]);
          }
        };

        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(debouncedEmail);

        if (
          recoveryMode === "email" &&
          debouncedEmail &&
          isValidEmail
        ) {
          fetchUsernamesByEmail();
        }
      }, [debouncedEmail, recoveryMode]);
  
    // Fetch phone numbers
    const {
      data: phoneNumbers,
      error: phoneError,
      isError,
    } = usePhoneNumbersByUsername(debouncedUsername);
    
    useEffect(() => {
      if (isError) {
        if (phoneError?.response?.status === 422) {
          toasters({ type: "error", message: "Username is not valid" });
    
          formikRef.current?.setFieldError("username", "Username is not valid");
        } else {
          toasters({ type: "error", message: "User not found" });
        }
    
        setPhoneOptionsList([]);
        return;
      }
    
      if (Array.isArray(phoneNumbers?.data)) {
        const options = phoneNumbers.data.map((item) => ({
          label: item.value,
          value: item.key,
        }));
        setPhoneOptionsList(options);
      } else {
        setPhoneOptionsList([]);
      }
    }, [phoneNumbers, isError, phoneError]);      
      
    const { mutate } = useMutation(sendForgotPassword, {
      onSuccess: (data, variables, context) => {
        // Some APIs wrap status in `data.status`
        const status = data?.status;
    
        if (status === 208) {
          // redirect to /resendEmail instead of /otp
          navigate('/resendEmail', {
            state: {
              username: variables?.username_tx,
              message: data?.message,
            },
          });
          return;
        }
    
        // Normal case → OTP sent
        toasters({ type: 'success', message: data?.message });
        navigate('/otp');
      },
      onError: (error) => {
        const errorMsg =
          error?.response?.data?.message || "Something went wrong";
        toasters(errorMsg);
    
        if (error?.response?.data?.errors) {
          console.error("Field errors:", error?.response?.data?.errors);
        }
      },
    });    

    useEffect(() => {
      if (!formikRef.current) return;
    
      // Whenever username changes, reset phone selection
      formikRef.current.setFieldValue("phoneOptions", "", false);
      formikRef.current.setFieldTouched("phoneOptions", false, false);
    }, [debouncedUsername]);    
      
        // keep it in sync with Formik
    useEffect(() => {
      const interval = setInterval(() => {
        const current = formikRef.current?.values?.recoveryOptions;
        if (current && current !== recoveryMode) {
          setRecoveryMode(current);
        }
      }, 50);
      return () => clearInterval(interval);
    }, [recoveryMode]);

    useEffect(() => {
      if (!formikRef.current) return;

      // Clear errors + touched immediately
      formikRef.current.setTouched({}, false);
      formikRef.current.setErrors({});

      if (recoveryMode === 'userName') {
        formikRef.current.setFieldValue('email', '', false);
      } else {
        formikRef.current.setFieldValue('username', '', false);
      }

      formikRef.current.setFieldValue('phoneOptions', '', false);
    }, [recoveryMode]);

    
  return (
    <div className="Auth-form-container">
        <Formik
          innerRef={formikRef}
          initialValues={{
            recoveryOptions: 'userName',
            username: '',
            email: '',
            phoneOptions: '',
          }}
          validationSchema={forgotPasswordSchema}
          validateOnChange={true}   
          validateOnBlur={true}   
          onSubmit={(values, { setTouched }) => {
            const payload = {
              username_tx: values.username,
              phone_tx: values.phoneOptions,
              email_tx: values.email || undefined,
            }
          
            // Dispatch Redux action here
            dispatch(setOtpSession(payload));
          
            mutate(payload);
            setTouched({});
          }}                
            
        >
          {({
            values,
            errors,
            touched,
            setFieldTouched,
            setFieldError,
            setFieldValue,
          }) => (
            <Form className="Auth-form">
              <h1 className="Auth-form-title text-white px-4 py-5">
                Forgot Password
              </h1>
              <div className="Auth-form-content p-4">
                <ReactSelect
                  name="recoveryOptions"
                  labelText={
                    <>
                      Select a Recovery Option
                      <span className="text-secondary ms-1">*</span>
                    </>
                  }
                  setFieldTouched={setFieldTouched}
                  setFieldError={setFieldError}
                  setFieldValue={setFieldValue}
                  options={recoveryOptions}
                  touched={touched}
                  errors={errors}
                  values={values}
                />
                <p className="text-danger errorMsg">
                  <ErrorMessage name="recoveryOptions" />
                </p>

                {values?.recoveryOptions === 'email' && (
                  <div className="mt-3">
                    <Field
                      name="email"
                      labelText="Email address"
                      type="email"
                      placeholder="johndoe@gmail.com"
                      component={Input}
                    />
                    <p className="text-danger errorMsg">
                      <ErrorMessage name="email" />
                    </p>
                  </div>
                )}

                {values?.recoveryOptions === 'userName' ? (
                  <div className="mt-3">
                    <Field
                      name="username"
                      labelText="User Name"
                      type="text"
                      placeholder="e.g Jane Doe"
                      component={Input}
                    />
                    <p className="text-danger errorMsg">
                      <ErrorMessage name="username" />
                    </p>
                  </div>
                ) : (
                  <div className="mt-3">
                    <ReactSelect
                      name="username"
                      labelText="Select Username"
                      options={usernameOptions}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      setFieldError={setFieldError}
                      values={values}
                      touched={touched}
                      errors={errors}
                    />
                    <p className="text-danger errorMsg">
                      <ErrorMessage name="username" />
                    </p>
                  </div>
                )}

                <div className="mt-3">
                  <ReactSelect
                    name="phoneOptions"
                    labelText={
                      <>
                        Select a Phone
                        <span className="text-secondary ms-1">*</span>
                      </>
                    }
                    setFieldTouched={setFieldTouched}
                    setFieldError={setFieldError}
                    setFieldValue={setFieldValue}
                    options={phoneOptionsList}
                    touched={touched}
                    errors={errors}
                    values={values}  // <== Passed in
                  />
                  <p className="text-danger errorMsg">
                    <ErrorMessage name="phoneOptions" />
                  </p>
                </div>

                <div className="d-grid gap-2 mt-3">
                  <button type="submit" className="btn btn-primary">
                  Submit
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
    </div>
  );
};

export default ForgotPassword;
